import React from "react";
import { User, MapPin, Shield, Clock, Calendar } from "react-feather";

// Reuses the icon-in-colored-circle language already established by the Dashboard's
// KpiCard (features/dashboard/components/KpiCard.jsx) - the only existing "icon avatar
// + label + value" pattern in this codebase - scaled down for a compact header row
// instead of a full metric tile. Shown identically on both Pending Deployments' own
// "Deployment Details" tab and the AI Guard Roster tab, so the same five fields the
// user called out as most important (customer, area, guards required, shift/type,
// requested date) always look the same regardless of which tab you're on.
const CARD_DEFS = [
  { key: "customer", icon: User, bg: "#6E1423" },
  { key: "area", icon: MapPin, bg: "#2E86AB" },
  { key: "guards", icon: Shield, bg: "#C0392B" },
  { key: "shift", icon: Clock, bg: "#8E44AD" },
  { key: "date", icon: Calendar, bg: "#1E8449" },
];

const DeploymentContextCards = ({ order, guardsCount, deploymentType, shift, requestedDate, requestedDateIsFallback }) => {
  const values = {
    customer: { label: "Customer", value: order.customerName, caption: order.customerPhone },
    area: { label: "Area / Site", value: order.area || "Not set", caption: order.branchCode },
    guards: { label: "Guards Required", value: guardsCount, caption: deploymentType ? titleCase(deploymentType) : null },
    // A request can hold Day and Night lines together ("Day + Night").
    shift: { label: "Shift", value: shift ? shift.split(" + ").map(titleCase).join(" + ") : "Any", caption: null },
    date: {
      label: "Requested Date",
      value: requestedDate ? new Date(requestedDate).toLocaleDateString() : "Not set",
      // The customer's own Requested Deployment Date is optional and often left unset at
      // order-confirm time - when that's the case, the requirement line's own Start Date is
      // used instead, and that substitution is shown here rather than silently presented as
      // if the customer actually gave this date.
      caption: requestedDateIsFallback ? "Line start date (no requested date on file)" : null,
    },
  };

  return (
    <div className="row g-2 mb-2">
      {CARD_DEFS.map(({ key, icon: Icon, bg }) => {
        const v = values[key];
        return (
          <div className="col-6 col-md" key={key}>
            <div
              className="card h-100"
              style={{ borderTop: `3px solid ${bg}`, background: `linear-gradient(180deg, ${hexToRgba(bg, 0.06)}, #fff 60%)` }}
            >
              <div className="card-body py-2 px-2">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                    style={{ width: 28, height: 28, backgroundColor: bg, color: "#fff", flexShrink: 0, boxShadow: `0 2px 6px ${hexToRgba(bg, 0.4)}` }}
                  >
                    <Icon size={14} />
                  </span>
                  <span className="text-muted small text-uppercase fw-semibold" style={{ fontSize: 11 }}>{v.label}</span>
                </div>
                <div className="fw-semibold text-truncate" title={String(v.value)}>{v.value}</div>
                {v.caption && <div className="small text-muted text-truncate">{v.caption}</div>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const hexToRgba = (hex, alpha) => {
  const n = parseInt(hex.replace("#", ""), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default DeploymentContextCards;
